import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import { ORGANIZATION_USER_ID } from "../../constants";

import { Toast } from "../../../../components/Toast";
import { field_IDs_mapping } from "../../notifications/fieldMapping";
export default function SendMicrosoftTeamsAdaptiveCardMessage() {
  const colors = useColors();
  const { fetchNotificationSetting, sendMicrosoftTeamsAdaptiveCardMessage } =
    useAPI();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [conversationID, setConversationID] = useState("");

  const defaultMessage = `[
    {
        "type": "TextBlock",
        "text": "Text for this TextBlock",
        "wrap": false
    }
]`;

  const [message, setMessage] = useState(defaultMessage);

  const conversationsSettingID = `${ORGANIZATION_USER_ID}-${IntegrationsMapping.microsoft_teams.codeName}-${field_IDs_mapping.MICROSOFT_TEAMS.CONVERSATIONS}`;

  const microsoftTeamsConversationsQuery = useQuery({
    queryKey: ["support-microsoftTeamsConversations"],
    queryFn: () => fetchNotificationSetting(conversationsSettingID),
    select: (response) => response.data,

    enabled: isModalOpen,
  });

  const conversations = microsoftTeamsConversationsQuery.data?.value;

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const sendMessageMutation = useMutation({
    mutationFn: async () => {
      // check if message is valid JSON
      try {
        JSON.parse(message);
      } catch (error) {
        throw new Error("Invalid JSON for adaptive card body");
      }
      await sendMicrosoftTeamsAdaptiveCardMessage(conversationID, message);
    },
    onSuccess: () => {
      Toast.success("Message sent successfully");
      handleClose();
    },
    onError: (error) => {
      Toast.error(error.message);
      handleClose();
    },
  });

  return (
    <>
      <Button
        variant="outlined"
        sx={{
          backgroundColor: colors.secondary_main,
          "&:hover": {
            backgroundColor: colors.secondary_dark,
          },
        }}
        color="primary"
        onClick={() => setIsModalOpen(true)}
      >
        Send Microsoft Teams Adaptive Card Message
      </Button>
      <Modal open={isModalOpen} onClose={handleClose}>
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 1000,
            maxHeight: "90vh",
            bgcolor: colors.common_white,
            borderRadius: 2,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "auto",
          }}
        >
          <Typography variant="h6">
            Send Microsoft Teams Adaptive Card Message
          </Typography>
          {microsoftTeamsConversationsQuery.isLoading ? (
            <LoadingIcon />
          ) : microsoftTeamsConversationsQuery.isSuccess ? (
            <>
              <Typography variant="body1">
                The message is sent using our Microsoft Teams{" "}
                <i>Breeze Security Platform</i> app (lab or prod), according to
                the AWS account the current customer runs on.
              </Typography>
              {conversations && conversations.length > 0 ? (
                <>
                  <Typography variant="body1">
                    Current conversation IDs set for this customer:
                  </Typography>
                  {conversations.map((conversation) => (
                    <Box
                      key={conversation.team_id}
                      display="flex"
                      flexDirection="column"
                      gap={0.5}
                    >
                      <Typography variant="body2" key={conversation.team_id}>
                        {conversation.team_name}{" "}
                      </Typography>
                      <Typography
                        variant="caption"
                        width="fit-content"
                        sx={{
                          padding: "8px",
                          backgroundColor: colors.grey_100,
                          border: `1px solid ${colors.black_32percent}`,
                          borderRadius: "4px",
                        }}
                      >
                        <code>{conversation.conversation_id}</code>
                      </Typography>
                    </Box>
                  ))}
                </>
              ) : (
                <Typography variant="body1">
                  No conversations set for this customer ID.
                </Typography>
              )}
              <TextField
                label="Conversation ID"
                value={conversationID}
                onChange={(e) => setConversationID(e.target.value)}
              />
              <TextField
                label="Message"
                multiline
                rows={8}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <ContainedButton
                onClick={() => sendMessageMutation.mutate()}
                color="primary"
                size="medium"
                disabled={!conversationID || !message}
                loading={sendMessageMutation.isPending}
              >
                Send Message
              </ContainedButton>
            </>
          ) : (
            <Typography variant="body1">
              Error loading conversations.
            </Typography>
          )}
        </Box>
      </Modal>
    </>
  );
}
